import { Box } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useDrop } from "react-dnd";
import {
  DND_ITEM_TYPES,
  DraggedProjectItem,
} from "../../../constant/dnd.constant";
import { palette } from "../../../styles/theme";

interface ConvertToTeamDropZoneProps {
  onProjectConvert: (projectId: string) => Promise<void>;
}

export const ConvertToTeamDropZone = ({
  onProjectConvert,
}: ConvertToTeamDropZoneProps) => {
  const [shouldShowExpanded, setShouldShowExpanded] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [{ isOver, canDrop, draggedItem }, drop] = useDrop<
    DraggedProjectItem,
    void,
    {
      isOver: boolean;
      canDrop: boolean;
      draggedItem: DraggedProjectItem | null;
    }
  >({
    accept: DND_ITEM_TYPES.PROJECT,
    canDrop: (item) => {
      return !!item.projectId;
    },
    drop: (item) => {
      if (onProjectConvert) {
        onProjectConvert(item.projectId).catch((error) => {
          console.error("❌ Error converting project:", error);
        });
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      draggedItem: monitor.getItem() as DraggedProjectItem | null,
    }),
  });

  const isActive = isOver && canDrop;

  useEffect(() => {
    if (isActive) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      setShouldShowExpanded(true);
    } else {
      if (shouldShowExpanded && !hideTimeoutRef.current) {
        hideTimeoutRef.current = setTimeout(() => {
          setShouldShowExpanded(false);
          hideTimeoutRef.current = null;
        }, 500);
      }
    }
  }, [isActive, shouldShowExpanded]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  if (!canDrop || !draggedItem) {
    return (
      <Box
        ref={drop as any}
        sx={{
          height: "4px",
          width: "100%",
        }}
      />
    );
  }

  return (
    <Box
      ref={drop as any}
      sx={{
        width: "100%",
        minHeight: "12px",
        maxHeight: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {shouldShowExpanded && (
        <Box
          sx={{
            height: "2px",
            width: "100%",
            backgroundColor: palette.blue.secondaryEffect,
            transition: "background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      )}
    </Box>
  );
};
